/**
 * TelefonatModal — Globales Modal fuer schnelle Telefonat-Erfassung (Plan 06-03 + 06-04).
 *
 * 2-Schritt-Flow:
 *   Schritt 1 — Lead-Suche: Suchfeld mit debounced Auto-Complete (300ms, ab 2 Zeichen).
 *               Max 8 Treffer. Klick auf Treffer -> Schritt 2 (existierender Lead).
 *               "Neuen Lead anlegen" Button -> Schritt 2 (neuer Lead, isNewLeadMode=true).
 *   Schritt 2a — Telefonat-Form fuer existierenden Lead:
 *               calledAt (datetime-local), durationMinutes (number), resultNote (textarea).
 *               Submit -> POST /leads/:id/call-log -> Erfolg-Meldung -> onClose().
 *   Schritt 2b — Neuer Lead aus Telefonat (isNewLeadMode=true):
 *               Lead-Form (vereinName Pflicht + optionale Felder) + Telefonat-Form.
 *               Submit -> POST /leads/create-with-call -> Erfolg-Meldung -> onClose().
 *               Hinweis: "Auto-Mails sind deaktiviert" (D-08).
 *
 * Keyboard-Navigation im Dropdown: ArrowDown/ArrowUp + Enter.
 * Backdrop-Klick schliesst Modal.
 *
 * CI-Farben: #E9FF57 + #1A1A1A (Submit), nie weisse Schrift auf Gelb.
 */

import { useState, useRef, useEffect } from 'react';
import { Phone, X, ArrowLeft, Loader2 } from 'lucide-react';
import { leadsApi } from '../services/leadsApi';
import type { Lead } from '../types';

// ── Status-Labels fuer Dropdown-Treffer (D-10) ──────────────────────────────
const STATUS_LABELS: Record<string, string> = {
  lead_form: 'Formular',
  followup_1: 'Follow-up 1',
  followup_2: 'Follow-up 2',
  in_kontakt: 'In Kontakt',
  positiv: 'Positiv',
  negativ: 'Negativ',
  negativ_hotlead: 'Neg. Hotlead',
};

function nowDatetimeLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface TelefonatModalProps {
  onClose: () => void;
  /** Callback fuer ggf. externe Steuerung — optional, kein Error wenn nicht gesetzt */
  onNewLead?: (initialQuery: string) => void;
}

export default function TelefonatModal({ onClose }: TelefonatModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // ── Neuer-Lead-Mode (Plan 06-04) ──────────────────────────────────────
  const [isNewLeadMode, setIsNewLeadMode] = useState(false);
  const [newLeadVname, setNewLeadVname] = useState('');
  const [newLeadPhone, setNewLeadPhone] = useState('');
  const [newLeadEmail, setNewLeadEmail] = useState('');
  const [newLeadContact, setNewLeadContact] = useState('');

  // Schritt-2-Form-State (geteilt von bestehendem Lead + neuem Lead)
  const [calledAt, setCalledAt] = useState<string>(nowDatetimeLocal());
  const [durationMinutes, setDurationMinutes] = useState<string>('5');
  const [resultNote, setResultNote] = useState<string>('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce-Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ── Schritt 1: Suchfeld-Logik ────────────────────────────────────────────
  const handleQueryChange = (val: string) => {
    setQuery(val);
    setHighlightIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await leadsApi.search(val.trim());
        setResults(res);
        setHighlightIndex(res.length > 0 ? 0 : -1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results.length > 0 && highlightIndex >= 0) {
      e.preventDefault();
      selectLead(results[highlightIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const selectLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsNewLeadMode(false);
    setStep(2);
    setCalledAt(nowDatetimeLocal());
    setDurationMinutes('5');
    setResultNote('');
    setError(null);
    setSuccess(false);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedLead(null);
    setIsNewLeadMode(false);
    setError(null);
    setSuccess(false);
  };

  // ── Neuer-Lead-Modus aktivieren ────────────────────────────────────────
  const handleNewLeadMode = () => {
    setIsNewLeadMode(true);
    setNewLeadVname(query.trim());
    setNewLeadPhone('');
    setNewLeadEmail('');
    setNewLeadContact('');
    setCalledAt(nowDatetimeLocal());
    setDurationMinutes('5');
    setResultNote('');
    setError(null);
    setSuccess(false);
    setStep(2);
  };

  // ── Schritt 2: Telefonat-Submit (existierender Lead) ──────────────────
  const durationNum = Number(durationMinutes);
  const isValid =
    !Number.isNaN(durationNum) &&
    durationNum > 0 &&
    resultNote.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || submitBusy || !selectedLead) return;
    setSubmitBusy(true);
    setError(null);
    try {
      await leadsApi.logCall(selectedLead.id, {
        calledAt: calledAt ? new Date(calledAt).toISOString() : undefined,
        durationMinutes: durationNum,
        resultNote: resultNote.trim(),
      });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setError(e?.message ?? 'Telefonat konnte nicht gespeichert werden');
    } finally {
      setSubmitBusy(false);
    }
  };

  // ── Schritt 2: Neuer-Lead-Submit (Plan 06-04) ─────────────────────────
  const isNewLeadValid =
    newLeadVname.trim().length > 0 &&
    !Number.isNaN(durationNum) &&
    durationNum > 0 &&
    resultNote.trim().length > 0;

  const handleNewLeadSubmit = async () => {
    if (!newLeadVname.trim()) {
      setError('Vereinsname ist Pflicht');
      return;
    }
    if (submitBusy) return;
    setSubmitBusy(true);
    setError(null);
    try {
      await leadsApi.createLeadWithCall({
        vereinName: newLeadVname.trim(),
        vereinEmail: newLeadEmail.trim() || null,
        vereinPhone: newLeadPhone.trim() || null,
        vereinContactPerson: newLeadContact.trim() || null,
        callDate: calledAt ? new Date(calledAt).toISOString() : new Date().toISOString(),
        callDurationMinutes: durationNum,
        callNotes: resultNote.trim() || null,
      });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setError(e?.message ?? 'Fehler beim Anlegen des Leads');
    } finally {
      setSubmitBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ────────────────────────────────────────────────────── */}
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <h2 className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Phone className="w-4 h-4" />
            {step === 2 && selectedLead && !isNewLeadMode
              ? selectedLead.vereinName
              : step === 2 && isNewLeadMode
              ? 'Neuer Lead anlegen'
              : 'Telefonat erfassen'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-[#1A1A1A] rounded p-1"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="p-4">

          {/* ── Schritt 1: Lead-Suche ─────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Verein, E-Mail oder Telefon..."
                  className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A] bg-white focus:outline-none focus:border-[#1A1A1A]"
                />
                {loading && (
                  <div className="absolute right-2.5 top-2.5">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>

              {/* Dropdown-Treffer */}
              {query.trim().length >= 2 && !loading && (
                <div className="border border-[#DAD9D9] rounded overflow-hidden">
                  {results.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 italic">
                      Kein Lead gefunden.
                    </div>
                  ) : (
                    results.map((lead, idx) => (
                      <button
                        key={lead.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-[#DAD9D9] last:border-0 ${
                          idx === highlightIndex ? 'bg-gray-100' : ''
                        }`}
                        onClick={() => selectLead(lead)}
                      >
                        <div className="font-semibold text-[#1A1A1A] text-sm">
                          {lead.vereinName}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                          {lead.vereinEmail && <span>{lead.vereinEmail}</span>}
                          <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-[10px]">
                            {STATUS_LABELS[lead.status] ?? lead.status}
                          </span>
                        </div>
                      </button>
                    ))
                  )}

                  {/* "Neuen Lead anlegen"-Button — immer sichtbar wenn query >= 2 */}
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t border-[#DAD9D9] font-medium"
                    onClick={handleNewLeadMode}
                  >
                    + Neuen Lead anlegen mit &ldquo;{query.trim()}&rdquo;
                  </button>
                </div>
              )}

              {query.trim().length < 2 && (
                <p className="text-xs text-gray-400">
                  Mind. 2 Zeichen eingeben um die Suche zu starten.
                </p>
              )}
            </div>
          )}

          {/* ── Schritt 2a: Telefonat-Form fuer existierenden Lead ──────── */}
          {step === 2 && selectedLead && !isNewLeadMode && (
            <div className="space-y-3">
              {/* Zurueck-Link */}
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1A1A1A] mb-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Anderen Lead suchen
              </button>

              {/* Erfolg-Meldung */}
              {success && (
                <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded text-sm font-medium">
                  Telefonat erfolgreich gespeichert.
                </div>
              )}

              {/* Fehler-Meldung */}
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {error}
                </div>
              )}

              {/* Form-Felder */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                    Zeitpunkt
                  </label>
                  <input
                    type="datetime-local"
                    value={calledAt}
                    onChange={(e) => setCalledAt(e.target.value)}
                    className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                    Dauer (Minuten)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    placeholder="Minuten"
                    className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                  Ergebnis-Notiz
                </label>
                <textarea
                  rows={3}
                  value={resultNote}
                  onChange={(e) => setResultNote(e.target.value)}
                  placeholder="Kurze Notiz zum Gespraech (optional)"
                  className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white resize-y"
                />
              </div>
            </div>
          )}

          {/* ── Schritt 2b: Neuer Lead aus Telefonat (Plan 06-04) ──────── */}
          {step === 2 && isNewLeadMode && (
            <div className="space-y-3">
              {/* Zurueck-Link */}
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1A1A1A] mb-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Zurueck zur Suche
              </button>

              {/* D-08 Hinweis: Auto-Mails deaktiviert */}
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-sm">
                Auto-Mails sind deaktiviert. Status nach Anlage aendern um Auto-Workflow zu starten.
              </div>

              {/* Erfolg-Meldung */}
              {success && (
                <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded text-sm font-medium">
                  Lead erfolgreich angelegt.
                </div>
              )}

              {/* Fehler-Meldung */}
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {error}
                </div>
              )}

              {/* Lead-Felder */}
              <div>
                <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                  Vereinsname <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newLeadVname}
                  onChange={(e) => setNewLeadVname(e.target.value)}
                  placeholder="z.B. VfB Musterstadt"
                  className="w-full px-3 py-2 text-sm border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A]"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Telefon</label>
                <input
                  type="tel"
                  value={newLeadPhone}
                  onChange={(e) => setNewLeadPhone(e.target.value)}
                  placeholder="+49 ..."
                  className="w-full px-3 py-2 text-sm border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#1A1A1A] mb-1">E-Mail</label>
                <input
                  type="email"
                  value={newLeadEmail}
                  onChange={(e) => setNewLeadEmail(e.target.value)}
                  placeholder="verein@example.de"
                  className="w-full px-3 py-2 text-sm border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Ansprechpartner</label>
                <input
                  type="text"
                  value={newLeadContact}
                  onChange={(e) => setNewLeadContact(e.target.value)}
                  placeholder="Name des Ansprechpartners"
                  className="w-full px-3 py-2 text-sm border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A]"
                />
              </div>

              {/* Trennlinie zum Telefonat-Teil */}
              <hr className="border-[#DAD9D9]" />
              <p className="text-xs font-medium text-[#1A1A1A]">Telefonat-Details</p>

              {/* Telefonat-Form — gleiche Felder wie Schritt 2a */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                    Zeitpunkt
                  </label>
                  <input
                    type="datetime-local"
                    value={calledAt}
                    onChange={(e) => setCalledAt(e.target.value)}
                    className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                    Dauer (Minuten)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    placeholder="Minuten"
                    className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
                  Ergebnis-Notiz
                </label>
                <textarea
                  rows={3}
                  value={resultNote}
                  onChange={(e) => setResultNote(e.target.value)}
                  placeholder="Kurze Notiz zum Gespraech (optional)"
                  className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white resize-y"
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Footer: Bestehender Lead ──────────────────────────────────── */}
        {step === 2 && selectedLead && !isNewLeadMode && (
          <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
            <button
              type="button"
              onClick={onClose}
              disabled={submitBusy}
              className="px-4 py-2 text-sm rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid || submitBusy || success}
              className="px-4 py-2 text-sm rounded bg-[#E9FF57] text-[#1A1A1A] font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitBusy ? 'Speichert...' : 'Speichern'}
            </button>
          </footer>
        )}

        {/* ── Footer: Neuer Lead (Plan 06-04) ──────────────────────────── */}
        {step === 2 && isNewLeadMode && (
          <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
            <button
              type="button"
              onClick={onClose}
              disabled={submitBusy}
              className="px-4 py-2 text-sm rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleNewLeadSubmit}
              disabled={!isNewLeadValid || submitBusy || success}
              className="px-4 py-2 text-sm rounded bg-[#E9FF57] text-[#1A1A1A] font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitBusy ? 'Speichert...' : 'Lead + Telefonat speichern'}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
